import clsx from 'clsx';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Activity } from 'src/components/assignments/activity';
import Breadcrumbs from 'src/components/common/breadcrumbs';
import { DomainBadge } from 'src/components/common/domain-badge';
import { EmptySlotDashIcon } from 'src/components/icons';
import { Tooltip } from 'src/components/tooltip';
import { useFormatAssignmentDueDate } from 'src/hooks/use-format-assignment-due-date';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { useTrimmedElementCheck } from 'src/hooks/use-trimmed-element-check';
import {
  AssignmentStatus,
  CardstackAssignment,
} from 'src/interfaces/assignment.types';
import AuthService from 'src/services/api/auth.service';
import {
  getAssignmentDomain,
  getAssignmentLessonNumber,
  getAssignmentLessonTitle,
  getCardstackAssignmentSubTitle,
  getContentStatus,
  getSortedCardstackActivities,
} from 'src/utils/assignments.utils';

import { AssignmentImage } from '../assignment-image';

import './cardstack-content.scss';

export interface Props {
  assignment: CardstackAssignment;
}

export const CardstackContent = ({ assignment }: Props) => {
  const { t } = useTranslation();

  const isMiddleSchoolView = useIsMiddleSchoolView();
  const formatAssignmentDueDate = useFormatAssignmentDueDate();

  const userBk = AuthService.getUserBk();

  const { content, dueDate, studentStatuses, isHandedInmCLASSAssessment } =
    assignment;
  const {
    domain,
    lessonThumbnailImagePath,
    lessonTitle,
    unitTitle,
    title,
    subject,
  } = content[0];

  const assignmentTitle = title ?? lessonTitle;

  const lessonNumber = getAssignmentLessonNumber(assignmentTitle);
  const lessonName = getAssignmentLessonTitle(assignmentTitle);

  const lessonNameRef = useRef<HTMLDivElement>(null);

  const isLessonNameTrimmed = useTrimmedElementCheck(lessonNameRef);

  const activityDueDate = dueDate ? new Date(dueDate) : null;
  const formattedDueDate = dueDate
    ? formatAssignmentDueDate(new Date(dueDate), isMiddleSchoolView)
    : null;

  const sortedActivities = getSortedCardstackActivities(content);

  const assignmentDomain = getAssignmentDomain(domain);

  const getActivityStatus = (
    activityContentBk: string | undefined,
  ): AssignmentStatus => {
    return getContentStatus(userBk, activityContentBk ?? '', studentStatuses);
  };

  return (
    <>
      <AssignmentImage
        imagePath={lessonThumbnailImagePath}
        classNames={clsx('cardstack-assignment__thumbnail', {
          'cardstack-assignment__thumbnail--middle-school': isMiddleSchoolView,
        })}
        locked={isHandedInmCLASSAssessment}
      />
      <div className="cardstack-assignment__info-container">
        {isMiddleSchoolView ? (
          <>
            <div
              className="cardstack-assignment__activity-caption
            cardstack-assignment__activity-caption--middle-school"
            >
              <div className="cardstack-assignment__main-caption">
                <div
                  data-testid="cardstack-assignment-subtitle"
                  className="cardstack-assignment__subtitle cardstack-assignment__subtitle--middle-school"
                >
                  <DomainBadge
                    domain={assignmentDomain}
                    isMiddleSchool
                    subject={subject}
                  />
                  <Breadcrumbs
                    unitTitle={unitTitle}
                    lessonNumber={lessonNumber}
                  />
                </div>
                <Tooltip
                  title={lessonName ?? ''}
                  classNames={{ tooltip: 'sh-mobile-tooltip-bottom-left' }}
                  suppress={!isLessonNameTrimmed}
                >
                  <div
                    ref={lessonNameRef}
                    className="cardstack-assignment__middle-school-title"
                    tabIndex={isLessonNameTrimmed ? 0 : -1}
                  >
                    {lessonName}
                  </div>
                </Tooltip>
              </div>
              <span className="cardstack-assignment__middle-school-grid-placeholder" />
              <span className="cardstack-assignment__middle-school-grid-placeholder" />
              <div className="cardstack-assignment__due-date">
                {formattedDueDate ? (
                  <>{formattedDueDate}</>
                ) : (
                  <EmptySlotDashIcon />
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              data-testid="cardstack-assignment-subtitle"
              className="cardstack-assignment__subtitle"
            >
              <DomainBadge
                domain={getAssignmentDomain(domain)}
                subject={subject}
              />
              <span className="cardstack-assignment__subtitle-text">
                {getCardstackAssignmentSubTitle(content[0])}
              </span>
            </div>
            <div className="cardstack-assignment__activity-caption">
              <div className="cardstack-assignment__activity-caption--left">
                {`${t('Activity')}:`}
              </div>
              <div>{`${t('Due')}:`}</div>
            </div>
          </>
        )}

        {sortedActivities.map((activity) => {
          return (
            <Activity
              key={activity.cardstackKey}
              content={activity}
              dueDate={activityDueDate}
              activityStatus={getActivityStatus(activity.contentBusinessKey)}
              assignment={assignment}
            />
          );
        })}
      </div>
    </>
  );
};
